import { createAction, handleActions } from 'redux-actions';
import { takeLatest, call, put } from 'redux-saga/effects';
import createRequestSaga, {
  createRequestActionTypes,
} from '../lib/createRequestSaga';
import * as authAPI from '../lib/api/auth';
import { finishLoading, startLoading } from './loading';
import { sendStoredPushTokenIfAny, deleteStoredPushTokenOnLogout } from '../lib/nativePush';

//액션 타입 정의
const [GET_COMPANIES, GET_COMPANIES_SUCCESS, GET_COMPANIES_FAILURE] =
  createRequestActionTypes('auth/GET_COMPANIES');

const [GET_NOTIES, GET_NOTIES_SUCCESS, GET_NOTIES_FAILURE] =
  createRequestActionTypes('auth/GET_NOTIES');

const [
  GET_PROJECT_COUNTS,
  GET_PROJECT_COUNTS_SUCCESS,
  GET_PROJECT_COUNTS_FAILURE,
] = createRequestActionTypes('auth/GET_PROJECT_COUNTS');

const [GET_BASECODES, GET_BASECODES_SUCCESS, GET_BASECODES_FAILURE] =
  createRequestActionTypes('auth/GET_BASECODES');

const [LOGIN, LOGIN_SUCCESS, LOGIN_FAILURE] =
  createRequestActionTypes('auth/LOGIN');

const TEMP_SET_USER = 'auth/TEMP_SET_USER';
const TEMP_SET_BASECODE = 'auth/TEMP_SET_BASECODE';

const LOGOUT = 'auth/LOGOUT';

//액션 생성 함수 생성
export const getCompanies = createAction(GET_COMPANIES);
export const getNoties = createAction(GET_NOTIES);
export const getBaseCodes = createAction(GET_BASECODES);
export const getProjectCounts = createAction(GET_PROJECT_COUNTS);
export const login = createAction(LOGIN, (dataToSubmit) => dataToSubmit);
export const tempSetUser = createAction(TEMP_SET_USER, (user) => user);
export const tempSetBaseCode = createAction(TEMP_SET_BASECODE, (data) => data);
export const logout = createAction(LOGOUT);

//사가 생성
const getCompaniesSaga = createRequestSaga(GET_COMPANIES, authAPI.getCompanies);
const getNotiesSaga = createRequestSaga(GET_NOTIES, authAPI.getNoties);
const getProjectCountsSaga = createRequestSaga(
  GET_PROJECT_COUNTS,
  authAPI.getProjectCounts,
);
const getBaseCodesSaga = createRequestSaga(GET_BASECODES, authAPI.getBaseCode);

//const loginSaga = createRequestSaga(LOGIN, authAPI.login);
function* logoutSaga() {
  // 네이티브 앱(Capacitor)에서만 동작, 웹은 no-op - access_token을 지우기 전에 호출해야
  // DELETE /api/v1/common/push-token 요청에 Authorization 헤더가 실린다.
  yield call(deleteStoredPushTokenOnLogout);

  // 2026-10-16: 서버의 리프레시 세션도 폐기해야 한다(안 그러면 그 기기는 리프레시 쿠키로
  // 계속 새 액세스 토큰을 받을 수 있음). 네트워크 에러 등으로 실패해도 로컬 로그아웃은
  // 그대로 진행 — 서버 쪽은 세션 만료(슬라이딩 TTL)로 결국 정리된다.
  try {
    yield call(authAPI.logout);
  } catch (e) {
    console.log('logout API 실패(로컬 로그아웃은 계속 진행):', e);
  }

  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
}

function* loginSaga(action) {
  yield put(startLoading(LOGIN));
  //파라미터로 action을 받아오면 액션의 정보를 조회 할 수 있다.
  try {
    //call 사용하면 Promise함수를 반환하는 함수를 호출하고 기다릴 수 있다.
    //첫번째 파라미터 함수는, 나머지 파라미터는 해당 함수에 넣을 인수이다.
    const tokenRes = yield call(authAPI.login, action.payload); //api.login(action.payload)의 의미
    const token = tokenRes.data.data;
    yield localStorage.setItem('access_token', JSON.stringify(token));

    const userRes = yield call(authAPI.getUser, {
      token,
      companyCd: action.payload.company,
    });
    yield localStorage.setItem('user', JSON.stringify(userRes.data));

    // 네이티브 앱에서만 동작(웹은 no-op) - 로그인 전(앱 기동 직후)에 이미 FCM 토큰을 받아뒀다면
    // 이제서야(Authorization 헤더를 실을 수 있게 된 시점에) 서버에 등록한다.
    yield call(sendStoredPushTokenIfAny);

    //공통코드 가져오기
    const baseRes = yield call(authAPI.getBaseCode, token);
    // yield localStorage.setItem('baseCodes', JSON.stringify(baseRes.data.list));

    //Noti가져오기
    const notieRes = yield call(authAPI.getNoties, token);

    yield put({
      type: LOGIN_SUCCESS,
      payload: {
        access_token: token,
        baseCodes: baseRes.data.list,
        user: userRes.data,
        noties: notieRes.data.list,
        notiCount: notieRes.data.count,
      },
    });
  } catch (e) {
    yield put({
      type: LOGIN_FAILURE,
      payload: e,
      error: true,
    });
  }
  yield put(finishLoading(LOGIN));
}

export function* authSaga() {
  //takeLatest : 기존에 진행 중이던 작업이 있다면 취소하고 가장 마지막 작업만 실행한다.
  yield takeLatest(GET_COMPANIES, getCompaniesSaga);
  yield takeLatest(LOGIN, loginSaga);
  yield takeLatest(LOGOUT, logoutSaga);
  yield takeLatest(GET_NOTIES, getNotiesSaga);
  yield takeLatest(GET_PROJECT_COUNTS, getProjectCountsSaga);
  yield takeLatest(GET_BASECODES, getBaseCodesSaga);
}

const initialState = {
  companies: [],
  companiesError: null,
  noties: [],
  notiCount: 0,
  notiesError: null,
  baseCodes: null,
  baseCodesError: null,
  projectCounts: [],
  countError: null,
  authLogin: null,
  authLoginError: null,
  user: null,
};

const auth = handleActions(
  {
    [GET_COMPANIES_SUCCESS]: (state, { payload: data }) => ({
      ...state,
      companies: data.companies,
      companiesError: null,
    }),
    [GET_COMPANIES_FAILURE]: (state, { payload: error }) => ({
      ...state,
      companiesError: error,
    }),
    [GET_NOTIES_SUCCESS]: (state, { payload: data }) => ({
      ...state,
      noties: data.list,
      notiCount: data.count,
      notiesError: null,
    }),
    [GET_NOTIES_FAILURE]: (state, { payload: error }) => ({
      ...state,
      notiesError: error,
    }),

    [GET_PROJECT_COUNTS_SUCCESS]: (state, { payload: data }) => ({
      ...state,
      projectCounts: data.list,
      countError: null,
    }),
    [GET_PROJECT_COUNTS_FAILURE]: (state, { payload: error }) => ({
      ...state,
      countError: error,
    }),

    [GET_BASECODES_SUCCESS]: (state, { payload: data }) => ({
      ...state,
      baseCodes: data.list,
    }),
    [GET_BASECODES_FAILURE]: (state, { payload: error }) => ({
      ...state,
      baseCodesError: error,
    }),
    [LOGIN_SUCCESS]: (state, { payload: data }) => ({
      ...state,
      authLoginError: null,
      authLogin: data.access_token,
      user: data.user,
      baseCodes: data.baseCodes,
      noties: data.noties,
      notiCount: data.notiCount,
    }),
    [LOGIN_FAILURE]: (state, { payload: error }) => ({
      ...state,
      authLoginError: error,
      user: null,
      baseCodes: null,
    }),
    [TEMP_SET_USER]: (state, { payload: user }) => ({
      ...state,
      user,
    }),
    [TEMP_SET_BASECODE]: (state, { payload: baseCodes }) => ({
      ...state,
      baseCodes,
    }),
    // [LOGOUT]: (state) => ({
    //   ...state,
    //   authLogin: null,
    //   authLoginError: null,
    //   user: null,
    //   baseCodes: null,
    // }),
    [LOGOUT]: () => initialState,
  },
  initialState,
);

export default auth;
